import { memo, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonDropdown,
  SegmentedControl,
  SpaceBetween,
} from '@amzn/awsui-components-core';

import { useInlineCommentManager } from '../../contexts/InlineCommentManagerContext';
import ActionPanelBase from './ActionPanelBase';
import { CommentCard } from './CommentCard';
import { CommentFilter, SourceFilter, useCommentsPanelViewModel } from './CommentsPanelViewModel';
import styles from './CommentsPanel.module.scss';

const CommentsPanel = memo(function CommentsPanel() {
  const vm = useCommentsPanelViewModel();
  const manager = useInlineCommentManager();
  const prevActiveThreadRef = useRef<string | null>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // When someone asks to reveal the comment, focus on the comment's input
  useEffect(() => {
    return manager.on('revealInSidebarRequested', (threadId) => {
      if (threadId === prevActiveThreadRef.current) {
        requestAnimationFrame(() => {
          const card = cardRefs.current.get(threadId);
          if (!card) return;
          const input = card.querySelector<HTMLInputElement>('input');
          input?.focus();
        });
      }
    });
  }, [manager]);

  // When the comment becomes active, scroll into it
  useEffect(() => {
    if (vm.isLoading) return;
    if (vm.activeThreadId === prevActiveThreadRef.current) return;

    prevActiveThreadRef.current = vm.activeThreadId;
    if (!vm.activeThreadId) return;

    cardRefs.current
      .get(vm.activeThreadId)
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [vm.isLoading, vm.activeThreadId]);

  return (
    <ActionPanelBase title="Comments">
      <SpaceBetween size="m" direction="vertical">
        <div className={styles.filterRow}>
          <SegmentedControl
            selectedId={vm.filter}
            onChange={({ detail }) => vm.setFilter(detail.selectedId as CommentFilter)}
            label="Comment filter"
            options={[
              { id: 'open', text: 'Open' },
              { id: 'resolved', text: 'Resolved' },
            ]}
          />
          <ButtonDropdown
            variant="inline-icon"
            ariaLabel="Filter by source"
            items={[
              { id: 'all', text: 'All comments', disabled: vm.sourceFilter === 'all' },
              { id: 'human', text: 'Human only', disabled: vm.sourceFilter === 'human' },
              { id: 'ai', text: 'AI only', disabled: vm.sourceFilter === 'ai' },
            ]}
            onItemClick={({ detail }) => vm.setSourceFilter(detail.id as SourceFilter)}
          />
        </div>

        {vm.isLoading && (
          <Box color="text-body-secondary" fontSize="body-s">
            Loading comments…
          </Box>
        )}

        {vm.loadErrorMessage !== undefined && (
          <Alert
            type="error"
            header="Couldn't load comments"
            action={<Button onClick={() => vm.retryLoad()}>Retry</Button>}
          >
            {vm.loadErrorMessage}
          </Alert>
        )}

        {!vm.isLoading && vm.loadErrorMessage === undefined && vm.visibleComments.length === 0 && (
          <Box textAlign="center" color="text-body-secondary" padding="l">
            No {vm.filter} comments
          </Box>
        )}

        {!vm.isLoading && vm.visibleComments.length > 0 && (
          <SpaceBetween size="s" direction="vertical">
            {vm.visibleComments.map((comment) => (
              <CommentCard
                key={comment.commentId}
                ref={(el) => {
                  if (el) cardRefs.current.set(comment.threadId, el);
                  else cardRefs.current.delete(comment.threadId);
                }}
                comment={comment}
                draftState={vm.draftState}
                reviewStatus={vm.reviewStatus}
                isSelected={vm.activeThreadId === comment.threadId}
                isResolved={vm.filter === 'resolved'}
                onClick={() => vm.toggleThread(comment.threadId)}
              />
            ))}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ActionPanelBase>
  );
});

export default CommentsPanel;
